import React, { useState, useEffect, useCallback, useRef, useMemo, Dispatch } from 'react';
import $i18n from '@/i18n';
import { Icon, Table, Input } from '@b-design/fusion';
import Button from '@/components/Button';
import { tableColumons } from './config.tsx'
import CalculateComponent from '@/components/CalculateComponent';
import PaginationComponent from '@/components/PaginationComponent';
import store from '@/store';
import './index.less';
import { useApplyDialog } from './useApplyDialog';
import { getJsApiApplySatusListForChannel } from '@/services/jsapiAuth'

interface IRecord {
  id: string;
  jsapiName: string;
  functionDesc: string;
  channelName: string;
  supportSystem: string[];
  controlStatus: "CONTROLLED" | "UNCONTROLLED"
}

export interface openJsapiAuthChannel {
  innerCode: string;
  name: string;
}

interface JsApiFeaturesProps {
  spaceId: string,
  accountId: string,
  appId: string,
  openJsApiAuthChannels: openJsapiAuthChannel[]
  currentTabKey: string,
  openApply: boolean;
  setOpenApply: (value: boolean) => void;
}

const JsApiFeatures: React.FC<JsApiFeaturesProps> = (props: JsApiFeaturesProps) => {
  const { openJsApiAuthChannels, spaceId, accountId, appId, currentTabKey, openApply, setOpenApply } = props
  const [userState] = store.useModel('user');

  // table数据
  const [dataSource, setDataSource] = useState<{ list: IRecord[], total: number }>({ list: [], total: 0 });
  // 内容区域高度
  const [contentHeight, setContentHeight] = useState(296);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('')

  const { openDialog } = useApplyDialog()

  // 分页参数
  const [paginationValue, setPaginationValue] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  // filter
  const [filterValue, setFilterValue] = useState(
    {
      "channelName": {
        "selectedKeys": [
          ""
        ]
      }
    }
  );

  const openJsApiAuthChannelsTransToMap = useMemo(() => {
    return openJsApiAuthChannels.reduce((acc, cur) => {
      acc[cur.innerCode] = cur.name
      return acc
    }, {})
  }, [openJsApiAuthChannels])

  const fetchData = (pageSize, pageNumber, channelCode?: string, jsapiName?: string) => {
    setLoading(true);
    /**
     *  channelCode=xx // 渠道code
        spaceId=xx // 空间code
        appId=xx // 小程序ID
        currentPage=1 // 当前页
        pageSize=10 // 一页几条
        tatus=xx // AUTHED(通过),IN_REVIEW（审核中）,REFUSED（已拒绝）
        jsapiName:""// 可选名称搜索
     */
    getJsApiApplySatusListForChannel({
      jsapiName: jsapiName ?? "",
      channelCode,
      spaceId,
      appId,
      currentPage: pageNumber,
      pageSize,
    }).then(async (res) => {
      if (res) {
        const newList = res?.list?.map((item) => {
          return {
            ...item,
            id: `${item.authId}`
          }
        })
        setDataSource({
          list: newList,
          total: res.total
        })
        setPaginationValue({
          currentPage: res.current,
          pageSize: res.pageSize
        })
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const refresh = () => {
    setFilterValue({
      "channelName": {
        "selectedKeys": [
          ""
        ]
      }
    })
    fetchData(paginationValue.pageSize, 1, filterValue.channelName.selectedKeys[0], inputValue)
  }
  
  useEffect(() => {
    if (currentTabKey === 'JsApiFeatures' && openApply) {
      openApplyDialog();
      setOpenApply(false)
    }
  }, [openApply, currentTabKey]);

  useEffect(() => {
    if (currentTabKey === 'JsApiFeatures') {
      fetchData(paginationValue.pageSize, 1, filterValue.channelName.selectedKeys[0], inputValue);
    }
  }, [currentTabKey])

  useEffect(() => {
    fetchData(paginationValue.pageSize, paginationValue.currentPage, filterValue.channelName.selectedKeys[0], inputValue)
  }, [paginationValue.currentPage, paginationValue.pageSize]);

  useEffect(() => {
    fetchData(paginationValue.pageSize, 1, filterValue.channelName.selectedKeys[0], inputValue)
  }, [filterValue])

  const openApplyDialog = () => {
    const titleStyle = {
      fontWeight: 500,
      fontSize: '16px',
      color: "#121C2D",
      letterSpacing: "0.15px",
      lineHeight: "24px"
    }

    const notSupportSyle = {
      fontWeight: 400,
      fontSize: '12px',
      color: "#B4BACC",
      letterSpacing: "0.4px",
      lineHeight: "24px",
      marginLeft: "16px"
    }
    openDialog({
      spaceId,
      appId,
      title: <div style={{ display: 'flex', flexFlow: 'row', alignItems: 'center' }}>
        <span style={titleStyle}>{$i18n.get({ 'id': "aopf.pages.miniapp.detail.tab.jsapiFeaturs.applyDialog.title", dm: "申请 JSAPI 功能" })}</span>
        <span style={notSupportSyle}>{$i18n.get({ id: "aopf.pages.miniapp.detail.tab.jsapiFeaturs.applyDialog.notSupportTip", dm: "本申请不支持 Uni-app 类型" })}</span>
      </div>,
      openJsApiAuthChannels,
      refresh
    });
  };

  const varStyle = {
    ['--table-body-height']: `${Math.max(200, contentHeight - 48)}px`,
  }

  const onSerch = (v) => {
    setFilterValue({
      "channelName": {
        "selectedKeys": [
          ""
        ]
      }
    })
    fetchData(paginationValue.pageSize, 1, filterValue.channelName.selectedKeys[0], inputValue)
  }

  const onkeydown = (e) => {
    if (e.code === 'Enter') {
      onSerch(inputValue)
    }
  }

  const onChange = (v) => {
    setInputValue(v)
  }

  const search = () => {
    return (
      <div onKeyDown={onkeydown}>
        <Input
          value={inputValue}
          onChange={onChange}
          placeholder={$i18n.get({ id: 'aopf.pages.miniapp.input.Search', dm: '搜索...' })}
          style={{ width: 184 }}
          innerBefore={<Icon type="search" style={{ margin: 4 }}

          />}
        />
      </div>
    )
  }
  
  return (
    <div className="global-filter-list">
      <div className="list-content">
        <div className="content-btns">
          <div className="btns-left" style={{ display: 'flex', flexFlow: 'row', alignItems: 'center' }}>
            <Button
              type="primary"
              data-child-type="icon-text"
              id="btn-create-miniapp"
              onClick={() => openApplyDialog()}
            >
              <Icon type="add--circle--filled" />
              {$i18n.get({ id: "aopf.pages.miniapp.detail.tab.jsapiFeaturs.btn.apply", dm: "申请" })}
            </Button>
            {search()}
          </div>
        </div>
        <div style={{ height: contentHeight, ...varStyle }}>

          <Table
            className="jsapi-authed-table"
            columns={tableColumons(openJsApiAuthChannelsTransToMap)}
            fixedHeader
            dataSource={dataSource?.list}
            primaryKey="id"
            loading={loading}
            filterParams={filterValue}
            onFilter={(filterParams) => {
              setFilterValue(filterParams);
            }}
          />
        </div>
        <PaginationComponent
          total={dataSource?.total}
          current={paginationValue?.currentPage}
          pageSize={paginationValue?.pageSize}
          onPageChange={(current, pageSize) => {
            setPaginationValue({
              currentPage: current,
              pageSize,
            });
          }}
        />

        {/* </Loading> */}
      </div>

      <CalculateComponent
        otherWidth={280}
        otherHeight={88 + 48 + 50 + 56 + 56}
        windowChange={(_width, height) => {
          setContentHeight(height);
        }}
      />
    </div>
  );
};

export default JsApiFeatures;
